import {
  Button,
  Card,
  CardContent,
  Divider,
  InputAdornment,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
} from "@material-ui/core";
import SearchIcon from "@material-ui/icons/SearchOutlined";
import { makeStyles } from "@material-ui/styles";
import * as Sentry from "@sentry/browser";
import clsx from "clsx";
import _ from "lodash";
import PropTypes from "prop-types";
import React, { useEffect, useState } from "react";
import PerfectScrollbar from "react-perfect-scrollbar";

import { DisableDestinationsModal } from "../destinations/modals/DisabledDestinationModal";

import { makeRequest } from "../util";

export const useDestinationListStyles = makeStyles((theme) => ({
  content: {
    padding: 0,
    "&:last-child": {
      paddingBottom: 0,
    },
  },
  inner: {
    minWidth: 700,
  },
  switchContainerHidden: {
    visibility: "hidden",
  },
  card: {
    width: "65%",
    minWidth: 800,
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(4),
  },
  cardHeader: {
    padding: theme.spacing(1),
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  navigation: {
    paddingTop: 24,
  },
  clickableImg: {
    "&:hover": {
      cursor: "pointer",
    },
  },
  closeIconAction: {
    "&:hover": {
      cursor: "pointer",
    },
    margin: 0,
  },
  destinationInfo: {
    display: "flex",
    alignItems: "center",
  },
  destinationName: {
    color: "#444",
    fontSize: 16,
    verticalAlign: "center",
    marginLeft: theme.spacing(2),
  },
  tableCell: {
    borderBottom: "none",
  },
  tableRow: {
    "&:hover": {
      backgroundColor: "#f0f0f0",
    },
  },
  tableRowClickable: {
    "&:hover": {
      cursor: "pointer",
      "& button": {
        backgroundColor: "rgba(63, 81, 181, 0.08)",
      },
    },
  },
  searchField: {},
}));

const DestinationListSearchInput = ({ initialSearch, onChange }) => {
  const classes = useDestinationListStyles();
  const [searchTerm, setSearchTerm] = useState(initialSearch);

  useEffect(() => {
    onChange(searchTerm);
  }, [searchTerm, onChange]);

  return (
    <TextField
      variant="outlined"
      name="search"
      autoComplete="off"
      onChange={(e) => setSearchTerm(e.target.value)}
      value={searchTerm}
      InputProps={{
        className: clsx(classes.searchField),
        placeholder: "Search",
        startAdornment: (
          <InputAdornment position="start">
            <SearchIcon className={clsx(classes.icon)} />
          </InputAdornment>
        ),
      }}
    />
  );
};

DestinationListSearchInput.propTypes = {
  initialSearch: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
};

const DestinationsList = ({
  destinations,
  configuredDestinations,
  onConfigure,
  onConfiguredDestsChange,
}) => {
  const classes = useDestinationListStyles();

  const [disableModalDest, setDisableModalDest] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");

  const handleSearchTermChange = (term) => {
    setSearchTerm(term.trim());
  };

  let filteredDestinations = [...destinations];
  if (searchTerm && searchTerm !== "") {
    filteredDestinations = _.filter(filteredDestinations, (dest) => {
      return (
        dest.name.toLowerCase().indexOf(searchTerm.toLowerCase()) !== -1 ||
        dest.key.toLowerCase().indexOf(searchTerm.toLowerCase()) !== -1
      );
    });
  }

  // Turns on or off the given destination based on the state.
  function setDestinationState(dest, state) {
    const id = configuredDestinations[dest.slug].id;

    makeRequest("toggle_whole_destination", { dest_id: id, state: state })
      .then(() => {
        const newDestState = _.extend({}, configuredDestinations[dest.slug], {
          enabled: state,
          id: id,
        });
        onConfiguredDestsChange({
          [dest.slug]: newDestState,
        });
      })
      .catch((error) => {
        alert.error("Something went wrong. Unable to toggle destintaion.");
        console.log(error);
        Sentry.captureException(error);
      });
  }

  function handleToggleDestination(dest) {
    const targetState = !configuredDestinations[dest.slug].enabled;

    if (targetState === false) {
      setDisableModalDest(dest);
    } else {
      setDestinationState(dest, true);
    }
  }

  return (
    <>
      <Card className={clsx(classes.card)}>
        <CardContent className={classes.content}>
          <div key="search-wrapper" className={classes.cardHeader}>
            <h4>Destinations</h4>
            <DestinationListSearchInput
              initialSearch={searchTerm}
              key="search"
              onChange={handleSearchTermChange}
            />
          </div>

          <Divider />
          <PerfectScrollbar>
            <div className={classes.inner}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Integration</TableCell>
                    <TableCell align="center">Status</TableCell>
                    <TableCell align="center">Settings</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredDestinations.map((dest) => {
                    const DestinationIcon = dest.logoSquare;
                    const enabled =
                      (configuredDestinations[dest.slug] &&
                        configuredDestinations[dest.slug].enabled) ||
                      false;
                    const showToggle =
                      configuredDestinations[dest.slug] &&
                      (configuredDestinations[dest.slug].is_valid_client_side ||
                        configuredDestinations[dest.slug].is_valid_server_side);

                    return (
                      <TableRow
                        key={dest.slug}
                        className={clsx(
                          classes.tableRow,
                          !showToggle && classes.tableRowClickable
                        )}
                        onClick={() => {
                          if (!showToggle) {
                            onConfigure(dest);
                          }
                        }}
                      >
                        <TableCell
                          className={clsx(classes.tableCell, "cursor-pointer")}
                          onClick={() => {
                            if (showToggle) {
                              onConfigure(dest);
                            }
                          }}
                        >
                          <div className={clsx(classes.destinationInfo)}>
                            {DestinationIcon && (
                              <DestinationIcon height={34} width={34} />
                            )}
                            <span className={classes.destinationName}>
                              {dest.name}
                            </span>
                          </div>
                        </TableCell>
                        <TableCell align="center" className={classes.tableCell}>
                          <div
                            className={clsx(
                              classes.switchContainer,
                              !showToggle && classes.switchContainerHidden
                            )}
                          >
                            <Switch
                              checked={enabled}
                              color="secondary"
                              edge="start"
                              title={`${enabled ? "Disable" : "Enable"} ${
                                dest.name
                              }`}
                              name={
                                "enable-destination-" +
                                (dest.slug in configuredDestinations &&
                                (configuredDestinations[dest.slug]
                                  .is_valid_client_side ||
                                  configuredDestinations[dest.slug]
                                    .is_valid_server_side)
                                  ? "visible"
                                  : "invisible")
                              }
                              onChange={() => handleToggleDestination(dest)}
                              value={enabled}
                            />
                          </div>
                        </TableCell>
                        <TableCell align="center" className={classes.tableCell}>
                          <Button
                            variant="outlined"
                            color="primary"
                            onClick={() => onConfigure(dest)}
                          >
                            Configure
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  {filteredDestinations.length === 0 &&
                    searchTerm &&
                    searchTerm !== "" && (
                      <TableRow>
                        <TableCell colSpan={3}>
                          No destinations found for &quot;{searchTerm}&quot;
                        </TableCell>
                      </TableRow>
                    )}
                </TableBody>
              </Table>
            </div>
          </PerfectScrollbar>
        </CardContent>
      </Card>
      <DisableDestinationsModal
        open={disableModalDest !== null}
        onClose={() => setDisableModalDest(null)}
        onConfirm={() => setDestinationState(disableModalDest, false)}
        dest={disableModalDest}
      />
    </>
  );
};

DestinationsList.propTypes = {
  destinations: PropTypes.array.isRequired,
  configuredDestinations: PropTypes.object.isRequired,
  onConfigure: PropTypes.func.isRequired,
  onConfiguredDestsChange: PropTypes.func.isRequired,
};

export default DestinationsList;
